import { createContext, useContext, useEffect, useState } from 'react'
import Header from './Header'
import Footer from './Footer'
import AdminNav from './AdminNav'
import ScrollToTop from '../ScrollToTop'
import { useAuthState } from '../../utils/authUtils'

interface DefaultLayoutProps {
  children: React.ReactNode
  variant?: 'public' | 'admin'
  reloadAuth?: boolean
}

const LayoutMountedContext = createContext(false)

const DefaultLayoutFrame = ({
  children,
  variant = 'public',
  reloadAuth = true
}: DefaultLayoutProps) => {
  const { reloadUser } = useAuthState()
  const isAdminRoute = variant === 'admin'
  const [adminNavCollapsed, setAdminNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('admin_nav_collapsed') === '1'
  })

  useEffect(() => {
    if (reloadAuth) reloadUser()
  }, [reloadAuth, reloadUser])

  useEffect(() => {
    if (!isAdminRoute) return

    const syncCollapsed = () => {
      setAdminNavCollapsed(window.localStorage.getItem('admin_nav_collapsed') === '1')
    }

    const onCollapsedChanged = (event: Event) => {
      const custom = event as CustomEvent<{ collapsed?: boolean }>
      if (typeof custom.detail?.collapsed === 'boolean') {
        setAdminNavCollapsed(custom.detail.collapsed)
        return
      }
      syncCollapsed()
    }

    syncCollapsed()
    window.addEventListener('admin-nav-collapsed-changed', onCollapsedChanged)
    return () => {
      window.removeEventListener('admin-nav-collapsed-changed', onCollapsedChanged)
    }
  }, [isAdminRoute])

  return (
    <LayoutMountedContext.Provider value>
      <ScrollToTop />
      <div
        className={
          isAdminRoute
            ? 'flex h-dvh min-h-0 flex-col overflow-hidden bg-background text-foreground'
            : 'min-h-screen bg-background text-foreground'
        }
      >
        {!isAdminRoute && <Header />}
        {isAdminRoute && <AdminNav />}
        <main
          data-route-scroll-container={isAdminRoute ? 'admin' : undefined}
          className={
            isAdminRoute
              ? `min-h-0 flex-1 overflow-y-auto overscroll-contain ${adminNavCollapsed ? 'md:pl-[84px]' : 'md:pl-[260px]'}`
              : ''
          }
        >
          {children}
        </main>
        {!isAdminRoute && <Footer />}
      </div>
    </LayoutMountedContext.Provider>
  )
}

const DefaultLayout = (props: DefaultLayoutProps) => {
  const isAlreadyMounted = useContext(LayoutMountedContext)
  if (isAlreadyMounted) return <>{props.children}</>
  return <DefaultLayoutFrame {...props} />
}

export default DefaultLayout
